import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { GamingSegment, SEGMENT_TYPE } from "../../constants/types";
import { PiHandTapFill } from "react-icons/pi";

interface ScratchCardProps {
  segment: GamingSegment;
  onScratchComplete: (segment: GamingSegment) => void;
  disabled?: boolean;
  width?: number;
  height?: number;
  revealThreshold?: number;
}

const ScratchCard: React.FC<ScratchCardProps> = ({
  segment,
  onScratchComplete,
  disabled = false,
  width = 300,
  height = 200,
  revealThreshold = 70,
}) => {
  const { t } = useTranslation("clientView");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isScratching, setIsScratching] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [hasStartedScratching, setHasStartedScratching] = useState(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || isRevealed) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#d4af37");
    gradient.addColorStop(0.25, "#c5a028");
    gradient.addColorStop(0.5, "#e6c547");
    gradient.addColorStop(0.75, "#b8960f");
    gradient.addColorStop(1, "#d4af37");

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    ctx.fillStyle = "rgba(255, 255, 255, 0.15)";
    ctx.fillRect(0, 0, width, 4);
    ctx.fillStyle = "rgba(0, 0, 0, 0.1)";
    ctx.fillRect(0, height - 4, width, 4);

    ctx.fillStyle = "rgba(255, 255, 255, 0.2)";
    for (let i = 0; i < 50; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      const size = Math.random() * 3 + 1;
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
    }
  }, [width, height, isRevealed]);

  const calculateScratchPercentage = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;

    const ctx = canvas.getContext("2d");
    if (!ctx) return 0;

    const imageData = ctx.getImageData(0, 0, width, height);
    const pixels = imageData.data;
    let transparentPixels = 0;

    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] === 0) {
        transparentPixels++;
      }
    }

    const totalPixels = width * height;
    return (transparentPixels / totalPixels) * 100;
  }, [width, height]);

  const scratch = useCallback(
    (x: number, y: number) => {
      const canvas = canvasRef.current;
      if (!canvas || isRevealed || disabled) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const rect = canvas.getBoundingClientRect();
      const canvasX = x - rect.left;
      const canvasY = y - rect.top;

      ctx.globalCompositeOperation = "destination-out";

      if (lastPointRef.current) {
        ctx.beginPath();
        ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
        ctx.lineTo(canvasX, canvasY);
        ctx.lineWidth = 40;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.stroke();
      }

      ctx.beginPath();
      ctx.arc(canvasX, canvasY, 20, 0, Math.PI * 2);
      ctx.fill();

      lastPointRef.current = { x: canvasX, y: canvasY };

      const percentage = calculateScratchPercentage();

      if (percentage >= revealThreshold && !isRevealed) {
        setIsRevealed(true);
        setTimeout(() => {
          onScratchComplete(segment);
        }, 3000);
      }
    },
    [
      isRevealed,
      disabled,
      calculateScratchPercentage,
      revealThreshold,
      segment,
      onScratchComplete,
    ]
  );

  const handleMouseDown = (e: React.MouseEvent) => {
    if (disabled || isRevealed) return;
    setIsScratching(true);
    setHasStartedScratching(true);
    lastPointRef.current = null;
    scratch(e.clientX, e.clientY);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isScratching || disabled || isRevealed) return;
    scratch(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    setIsScratching(false);
    lastPointRef.current = null;
  };

  const handleMouseLeave = () => {
    setIsScratching(false);
    lastPointRef.current = null;
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (disabled || isRevealed) return;
    e.preventDefault();
    setIsScratching(true);
    setHasStartedScratching(true);
    lastPointRef.current = null;
    const touch = e.touches[0];
    scratch(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isScratching || disabled || isRevealed) return;
    e.preventDefault();
    const touch = e.touches[0];
    scratch(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = () => {
    setIsScratching(false);
    lastPointRef.current = null;
  };

  const isPrize = segment.type === SEGMENT_TYPE.PRIZE;

  return (
    <div className="flex flex-col items-center gap-4" translate="no">
      <div
        className="relative rounded-xl overflow-hidden"
        style={{
          width,
          height,
          boxShadow:
            "inset 0 2px 4px rgba(255,255,255,0.4), inset 0 -2px 4px rgba(0,0,0,0.2), 0 10px 40px rgba(212, 175, 55, 0.3), 0 4px 12px rgba(0,0,0,0.3)",
        }}
      >
        <div className="absolute inset-0">
          {segment.image_url && (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(${segment.image_url})`,
                filter: "blur(2px) brightness(0.8)",
              }}
            />
          )}

          <div
            className={`absolute inset-0 ${
              segment.image_url
                ? isPrize
                  ? "bg-gradient-to-br from-amber-400/60 via-amber-500/70 to-amber-500/60"
                  : "bg-gradient-to-br from-gray-400/60 via-gray-500/70 to-gray-600/60"
                : isPrize
                ? "bg-gradient-to-br from-amber-400 via-amber-500 to-amber-500"
                : "bg-gradient-to-br from-gray-400 via-gray-500 to-gray-600"
            }`}
          />

          <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
            {isPrize ? (
              <>
                <div className="text-5xl mb-2">🎉</div>
                <h3 className="text-2xl font-bold text-white text-center mb-1 drop-shadow-lg">
                  {t("games.scratch.youWin")}
                </h3>
                <p className="text-lg text-white text-center font-medium drop-shadow-lg">
                  {segment.display_text}
                </p>
                {segment.gift_description && (
                  <p className="text-sm text-white/90 text-center mt-1 drop-shadow-md">
                    {segment.gift_description}
                  </p>
                )}
              </>
            ) : (
              <>
                <div className="text-5xl mb-2">😔</div>
                <h3 className="text-xl font-bold text-white text-center drop-shadow-lg">
                  {t("games.scratch.noWin")}
                </h3>
                <p className="text-white/90 text-center mt-1 drop-shadow-md">
                  {segment.display_text}
                </p>
              </>
            )}
          </div>
        </div>

        {!isRevealed && (
          <>
            <canvas
              ref={canvasRef}
              width={width}
              height={height}
              className={`absolute inset-0 ${
                disabled ? "cursor-not-allowed" : "cursor-pointer"
              }`}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            />
            {!hasStartedScratching && (
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <PiHandTapFill className="w-10 h-10 text-amber-900 mb-2 drop-shadow-lg" />
                <p className="text-lg font-bold text-amber-900 drop-shadow-sm">
                  {t("games.scratch.scratchHere")}
                </p>
              </div>
            )}
          </>
        )}

        {isRevealed && isPrize && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {Array.from({ length: 20 }).map((_, i) => (
              <div
                key={i}
                className="absolute animate-ping"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${1 + Math.random()}s`,
                }}
              >
                ✨
              </div>
            ))}
          </div>
        )}
      </div>

      {!isRevealed && !disabled && (
        <p className="text-gray-500 text-sm text-center">
          {t("games.scratch.instructions")}
        </p>
      )}
    </div>
  );
};

export default ScratchCard;
